import i18next from 'i18next'
import React from 'react'
import { Link } from 'widgets-for-react'
import { ComponentTeaserData } from '../site/root/components/componentTeasers/componentTeasersState'
import { PictureLayout } from '../site/root/image/imageLayout'
import { ImageData } from '../site/root/image/imageState'
import { DataLayer } from './datalayer'
import { componentDetailPath } from './pages'
import { SVGIcon } from './svgicon'

type ComponentTeaserProps = {
  teaser: ComponentTeaserData
  kind: 'link-only' | 'link-save' | 'link-delete'
  isSaved: boolean | 'unknown'
  onSaveOrDelete: () => void
  isComparableComponents: boolean
}

const TeaserInner = (props: {
  kind: ComponentTeaserProps['kind']
  imageData?: ImageData
  extraJSX?: React.ReactNode
}) => (
  <div className="teaser__inner">
    <div className="teaser__triangle" />
    <div className="teaser__visual fit-object">
      {props.imageData && (
        <PictureLayout.Img
          image={props.imageData}
          width="180"
          height="200"
          extraClasses="teaser__image"
          lazyload
        />
      )}
      {props.kind == 'link-only' && (
        <SVGIcon iconId="arrow" className="teaser__icon" size={16} />
      )}
    </div>
    {props.extraJSX}
  </div>
)

const TeaserContent = (props: {
  title: string
  isOwnComponent: boolean
  isDraft: boolean
}) => (
  <div className="teaser__content">
    {props.isOwnComponent && <TeaserShadowLabel {...props} />}
    <h3
      className="teaser__title"
      dangerouslySetInnerHTML={{ __html: props.title }}
    />
  </div>
)

const TeaserShadowLabel = (props: { isDraft: boolean }) => (
  <p
    className="teaser__label label--m"
    title={
      props.isDraft
        ? i18next.t('common:draft')
        : i18next.t('component:myComponent')
    }
  >
    <SVGIcon iconId={props.isDraft ? 'concept' : 'user2'} size={12} />
    <span className="sr-text">
      {props.isDraft
        ? i18next.t('common:draft')
        : i18next.t('component:myComponent')}
    </span>
  </p>
)

export const ComponentTeaserLayout = (p: ComponentTeaserProps) => {
  const teaserClasses = `teaser--component teaser--${p.kind}${
    p.kind !== 'link-only' ? ' teaser--link-button' : ''
  }${
    p.kind === 'link-save'
      ? p.isSaved === true
        ? ' teaser--saved'
        : ' teaser--unsaved'
      : ''
  }${p.teaser.IsUserComponent ? ' teaser--own' : ''} block__item`
  return p.kind === 'link-only' ? (
    <li className={teaserClasses} key={p.teaser.Id}>
      <article className="teaser__inner">
        <Link
          to={componentDetailPath(p.teaser.Id, p.teaser.Name)}
          className="teaser__link"
          onClick={() => {
            if (p.isComparableComponents)
              DataLayer.goToComparableComponent(p.teaser.Name)
          }}
        >
          <TeaserContent
            title={p.teaser.Name}
            isOwnComponent={p.teaser.IsUserComponent}
            isDraft={p.teaser.IsDraft}
          />
          <TeaserInner
            kind="link-only"
            imageData={p.teaser.IsUserComponent ? undefined : p.teaser.ImageURL}
          />
        </Link>
      </article>
    </li>
  ) : (
    <li className={teaserClasses} key={p.teaser.Id}>
      <Link
        to={componentDetailPath(p.teaser.Id, p.teaser.Name)}
        className="teaser__link"
        onClick={() => {
          if (p.isComparableComponents)
            DataLayer.goToComparableComponent(p.teaser.Name)
        }}
      >
        <TeaserContent
          title={p.teaser.Name}
          isOwnComponent={p.teaser.IsUserComponent}
          isDraft={p.teaser.IsDraft}
        />
      </Link>
      <TeaserInner
        kind={p.kind}
        imageData={p.teaser.IsUserComponent ? undefined : p.teaser.ImageURL}
        extraJSX={
          <div className="teaser__mask">
            {p.isSaved !== 'unknown' &&
              (p.kind === 'link-save' ? (
                <svg
                  viewBox="0 0 55 63"
                  role="button"
                  aria-pressed={p.isSaved === true}
                  className="teaser__button"
                  aria-labelledby={`a11y-component-teaser-button-${p.teaser.Id}`}
                  onClick={() => {
                    p.onSaveOrDelete()
                    if (p.isComparableComponents)
                      DataLayer.saveComparableComponent(p.teaser.Name)
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      p.onSaveOrDelete()
                      if (p.isComparableComponents)
                        DataLayer.saveComparableComponent(p.teaser.Name)
                    }
                  }}
                  tabIndex={0}
                >
                  <title id={`a11y-component-teaser-button-${p.teaser.Id}`}>
                    {p.isSaved === true
                      ? i18next.t('component:saveComponentInAnotherCollection')
                      : i18next.t('component:saveComponent')}
                  </title>
                  <path d="M0 32.66L0 32.86L27.5 48.81L55 64.76L55 0.76L0 32.66Z" />
                  <use
                    className="teaser__icon teaser__icon--unsaved"
                    width="18px"
                    height="18px"
                    transform="translate(29 25)"
                    xlinkHref="#icon--bookmark-ghost"
                  />
                  <use
                    className="teaser__icon teaser__icon--saved"
                    width="18px"
                    height="18px"
                    transform="translate(29 25)"
                    xlinkHref="#icon--bookmark-full"
                  />
                </svg>
              ) : (
                <svg
                  viewBox="0 0 55 63"
                  role="button"
                  className="teaser__button"
                  aria-labelledby={`a11y-component-teaser-button-${p.teaser.Id}`}
                  onClick={p.onSaveOrDelete}
                  tabIndex={0}
                >
                  <title id={`a11y-component-teaser-button-${p.teaser.Id}`}>
                    {i18next.t('component:deleteComponentLong')}
                  </title>
                  <path d="M0 32.66L0 32.86L27.5 48.81L55 64.76L55 0.76L0 32.66Z" />
                  <use
                    className="teaser__icon"
                    width="21px"
                    height="21px"
                    transform="translate(27 23)"
                    xlinkHref="#icon--delete"
                  />
                </svg>
              ))}
          </div>
        }
      />
    </li>
  )
}
